import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Plus, IndianRupee, FileText, Send, CheckCircle2, Receipt } from "lucide-react";

const STATUS_BADGE = {
  draft: "bg-[#F1EDDF] text-[#4A5A50]",
  sent: "bg-[#FFF3D6] text-[#7A4F08]",
  paid: "bg-[#E1F0E6] text-[#0F5E39]",
  cancelled: "bg-red-50 text-red-700",
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [orders, setOrders] = useState([]);
  const canEdit = can(user, "invoices.edit");

  const load = async () => {
    const { data } = await api.get("/invoices");
    setRows(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/orders", { params: { page: 1, page_size: 100 } }); setOrders(data.items || []); } catch {}
  })(); }, []);

  const doAction = async (id, action) => {
    try { await api.post(`/invoices/${id}/${action}`); toast.success(`Invoice ${action}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Accounts</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Invoices</h1>
          <p className="text-[#4A5A50] mt-1">Generated from completed orders. GST @ 3%.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpenNew(true)} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-invoice-btn">
            <Plus size={14} className="mr-1"/> Generate Invoice
          </Button>
        )}
      </div>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-0 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F1EDDF] text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <tr>
                <th className="px-4 py-3 text-left">Invoice #</th>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-right">Subtotal</th>
                <th className="px-4 py-3 text-right">Tax</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4DFCF]">
              {rows.map(inv => (
                <tr key={inv.id} className="hover:bg-[#FAF7EF]" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-medium">{inv.invoice_number}</td>
                  <td className="px-4 py-3">{inv.order_reference}</td>
                  <td className="px-4 py-3">{inv.client_name || "—"}</td>
                  <td className="px-4 py-3 text-right"><IndianRupee size={11} className="inline mr-0.5"/>{(inv.subtotal || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right">₹{(inv.tax_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right font-medium text-[#6E5416]">₹{(inv.total || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_BADGE[inv.status]}`}>{inv.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {inv.status === "draft" && canEdit && (
                        <Button size="sm" variant="outline" onClick={() => doAction(inv.id, "send")} className="rounded-full" data-testid={`invoice-send-${inv.id}`}>
                          <Send size={12} className="mr-1"/> Send
                        </Button>
                      )}
                      {inv.status === "sent" && canEdit && (
                        <Button size="sm" onClick={() => doAction(inv.id, "paid")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid={`invoice-paid-${inv.id}`}>
                          <CheckCircle2 size={12} className="mr-1"/> Mark Paid
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[#4A5A50]"><Receipt size={22} className="text-[#C9A24B] mx-auto mb-2"/>No invoices yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {canEdit && (
        <GenerateInvoiceDialog open={openNew} setOpen={setOpenNew} orders={orders} onCreated={load}/>
      )}
    </div>
  );
}

function GenerateInvoiceDialog({ open, setOpen, orders, onCreated }) {
  const [orderId, setOrderId] = useState("");
  const submit = async () => {
    if (!orderId) return toast.error("Pick an order");
    try { await api.post(`/invoices?order_id=${orderId}`); toast.success("Invoice generated"); setOpen(false); setOrderId(""); onCreated(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Generate Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Order</Label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger className="mt-1" data-testid="invoice-order-select"><SelectValue placeholder="Select order"/></SelectTrigger>
              <SelectContent>{orders.map(o => <SelectItem key={o.id} value={o.id}>{o.reference_number} · {o.client_name || "—"} · {o.status}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="text-xs text-[#4A5A50]">
            Totals are computed from order weight × current gold rate + CAD charges. Tax at 3% GST.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="invoice-generate-submit">Generate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
